// File: components/PropertyPanels/EnhancedConditionNodeProperties.jsx
import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Select,
  Button,
  FormLabel,
  Divider,
  SimpleGrid,
  Flex,
  Icon,
  RadioGroup,
  Radio,
  Checkbox,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import {
  FaSave,
  FaCodeBranch,
  FaChartBar,
  FaUser,
  FaFlag,
  FaCheck,
  FaTimes,
} from 'react-icons/fa';

const EnhancedConditionNodeProperties = ({ nodeConfig, setNodeConfig, updateNodeData, setSelectedNode }) => {
  return (
    <VStack spacing={4} align="stretch">
      {/* Condition Summary Preview */}
      {nodeConfig.conditionType && (
        <Box 
          p={3} 
          bgGradient="linear(to-br, orange.50, yellow.50)" 
          border="2px solid" 
          borderColor="orange.200" 
          borderRadius="xl"
        >
          <Flex align="center" gap={2} mb={2}>
            <Icon as={FaCodeBranch} color="orange.500" />
            <Text fontSize="xs" fontWeight="medium" color="orange.900">
              Condition Summary
            </Text>
          </Flex>
          <Box 
            fontSize="xs" 
            bg="white" 
            px={2} 
            py={1.5} 
            borderRadius="md" 
            border="1px solid" 
            borderColor="orange.200" 
            fontWeight="medium" 
            color="orange.900"
          >
            {nodeConfig.conditionType === 'conversion' && 
              `Conversion: ${nodeConfig.conversionDefinition || 'Not set'}`}
            {nodeConfig.conditionType === 'journey' && 
              `Journey: ${nodeConfig.journeyState || 'Not set'}`}
            {nodeConfig.conditionType === 'profile' && 
              `Profile: ${nodeConfig.fieldSelector || 'Not set'}`}
            {nodeConfig.conditionType === 'count' && 
              `Count: ${nodeConfig.eventName || 'Event'} ${nodeConfig.countOperator || '>'} ${nodeConfig.countValue || '0'}`}
          </Box>
          <Text fontSize="xs" color="orange.700" mt={2} fontStyle="italic">
            This condition uses pre-computed lifecycle state for fast evaluation.
          </Text>
        </Box>
      )}

      <Divider />

      {/* Condition Type - 3 Pill Tabs */}
      <Box>
        <FormLabel fontSize="xs" mb={2}>Condition Type</FormLabel>
        <SimpleGrid columns={3} spacing={1.5}>
          {[
            { value: 'conversion', label: 'Conversion Status', icon: FaChartBar },
            { value: 'profile', label: 'Profile/Attribute', icon: FaUser },
            { value: 'journey', label: 'Journey Flags', icon: FaFlag },
          ].map((type) => (
            <Button
              key={type.value}
              onClick={() => setNodeConfig({ ...nodeConfig, conditionType: type.value })}
              size="sm"
              h="auto"
              py={2}
              px={2}
              flexDirection="column"
              gap={1}
              border="2px solid"
              borderColor={nodeConfig.conditionType === type.value ? 'blue.500' : 'gray.200'}
              bg={nodeConfig.conditionType === type.value ? 'blue.50' : 'white'}
              color={nodeConfig.conditionType === type.value ? 'blue.500' : 'gray.700'}
              fontWeight={nodeConfig.conditionType === type.value ? 'medium' : 'normal'}
              _hover={{
                borderColor: nodeConfig.conditionType === type.value ? 'blue.500' : 'gray.300',
                bg: nodeConfig.conditionType === type.value ? 'blue.50' : 'gray.50',
              }}
            >
              <Icon as={type.icon} boxSize={3.5} />
              <Text fontSize="10px" lineHeight="tight" textAlign="center">
                {type.label}
              </Text>
            </Button>
          ))}
        </SimpleGrid>
      </Box>

      <Divider />

      {/* Conversion Status Configuration */}
      {nodeConfig.conditionType === 'conversion' && (
        <VStack spacing={4} align="stretch">
          <Box>
            <FormLabel fontSize="sm">Conversion Definition</FormLabel>
            <Select
              value={nodeConfig.conversionDefinition || ''}
              onChange={(e) => setNodeConfig({ ...nodeConfig, conversionDefinition: e.target.value })}
              placeholder="Select conversion"
              size="sm"
            >
              <option value="cart_conversion">Cart Conversion</option>
              <option value="signup_completed">Signup Completed</option>
              <option value="payment_success">Payment Success</option>
              <option value="lead_captured">Lead Captured</option>
            </Select>
          </Box>

          <Box>
            <FormLabel fontSize="sm">Match Entity By</FormLabel>
            <Select
              value={nodeConfig.matchEntityBy || 'order_id'}
              onChange={(e) => setNodeConfig({ ...nodeConfig, matchEntityBy: e.target.value })}
              size="sm"
            >
              <option value="order_id">order_id</option>
              <option value="session_id">session_id</option>
              <option value="user_id">user_id</option>
              <option value="custom">Custom Field</option>
            </Select>
          </Box>

          <Box p={3} bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="lg">
            <Text fontSize="xs" fontWeight="medium" color="purple.900" mb={2}>
              Entity Mapping
            </Text>
            <Box 
              fontSize="xs" 
              fontFamily="mono" 
              bg="white" 
              px={2} 
              py={1.5} 
              borderRadius="md" 
              border="1px solid" 
              borderColor="purple.200"
            >
              abandoned_cart.{nodeConfig.matchEntityBy || 'order_id'} = cart_converted.{nodeConfig.matchEntityBy || 'order_id'}
            </Box>
          </Box>

          <Box>
            <FormLabel fontSize="sm">Attribution Window</FormLabel>
            <SimpleGrid columns={2} spacing={2}>
              <Input
                type="number"
                min={1}
                value={nodeConfig.attributionValue || '24'}
                onChange={(e) => setNodeConfig({ ...nodeConfig, attributionValue: e.target.value })}
                placeholder="24"
                size="sm"
              />
              <Select
                value={nodeConfig.attributionUnit || 'hours'}
                onChange={(e) => setNodeConfig({ ...nodeConfig, attributionUnit: e.target.value })}
                size="sm"
              >
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </Select>
            </SimpleGrid>
          </Box>

          <Box>
            <FormLabel fontSize="sm">Result to Check</FormLabel>
            <RadioGroup
              value={nodeConfig.conversionResult || 'converted'}
              onChange={(value) => setNodeConfig({ ...nodeConfig, conversionResult: value })}
            >
              <VStack align="start" spacing={2}>
                <Radio value="converted" size="sm">Converted</Radio>
                <Radio value="not_converted" size="sm">Not Converted</Radio>
              </VStack>
            </RadioGroup>
          </Box>

          <Alert status="info" fontSize="xs" borderRadius="md">
            <AlertIcon boxSize={3} />
            <AlertDescription>
              💡 Conversion definitions are managed centrally in Conversion Settings.
            </AlertDescription>
          </Alert>
        </VStack>
      )}

      {/* Journey State Configuration */}
      {nodeConfig.conditionType === 'journey' && (
        <VStack spacing={4} align="stretch">
          <Box>
            <FormLabel fontSize="sm">Journey State</FormLabel>
            <Select
              value={nodeConfig.journeyState || ''}
              onChange={(e) => setNodeConfig({ ...nodeConfig, journeyState: e.target.value })}
              placeholder="Select state"
              size="sm"
            >
              <option value="has_purchased">Has Purchased Before</option>
              <option value="first_time_buyer">Is First-Time Buyer</option>
              <option value="active_subscription">Has Active Subscription</option>
              <option value="pending_cart">Has Pending Cart</option>
              <option value="returning_visitor">Is Returning Visitor</option>
              <option value="custom">Custom State</option>
            </Select>
          </Box>
        </VStack>
      )}

      {/* Profile/Attribute Configuration */}
      {nodeConfig.conditionType === 'profile' && (
        <VStack spacing={4} align="stretch">
          <Box>
            <FormLabel fontSize="sm">Attribute Source</FormLabel>
            <Select
              value={nodeConfig.attributeSource || 'profile'}
              onChange={(e) => setNodeConfig({ ...nodeConfig, attributeSource: e.target.value })}
              size="sm"
            >
              <option value="profile">User Profile</option>
              <option value="event">Event Attribute</option>
              <option value="message">Message Variable</option>
            </Select>
          </Box>

          <Box>
            <FormLabel fontSize="sm">Field Selector</FormLabel>
            <Input
              value={nodeConfig.fieldSelector || ''}
              onChange={(e) => setNodeConfig({ ...nodeConfig, fieldSelector: e.target.value })}
              placeholder="e.g., email, phone, custom_field"
              size="sm"
            />
          </Box>

          <Box>
            <FormLabel fontSize="sm">Operator</FormLabel>
            <Select
              value={nodeConfig.profileOperator || 'equals'}
              onChange={(e) => setNodeConfig({ ...nodeConfig, profileOperator: e.target.value })}
              size="sm"
            >
              <option value="equals">=</option>
              <option value="not_equals">!=</option>
              <option value="greater">&gt;</option>
              <option value="less">&lt;</option>
              <option value="contains">contains</option>
            </Select>
          </Box>

          <Box>
            <FormLabel fontSize="sm">Value</FormLabel>
            <Input
              value={nodeConfig.profileValue || ''}
              onChange={(e) => setNodeConfig({ ...nodeConfig, profileValue: e.target.value })}
              placeholder="Enter value"
              size="sm"
            />
          </Box>
        </VStack>
      )}

      {/* Event Count Configuration */}
      {nodeConfig.conditionType === 'count' && (
        <VStack spacing={4} align="stretch">
          <Box>
            <FormLabel fontSize="sm">Event Name</FormLabel>
            <Select
              value={nodeConfig.eventName || ''}
              onChange={(e) => setNodeConfig({ ...nodeConfig, eventName: e.target.value })}
              placeholder="Select event"
              size="sm"
            >
              <option value="cart_abandoned">Cart Abandoned</option>
              <option value="product_viewed">Product Viewed</option>
              <option value="purchase_completed">Purchase Completed</option>
              <option value="email_opened">Email Opened</option>
            </Select>
          </Box>

          <Box>
            <FormLabel fontSize="sm">Count Operator</FormLabel>
            <Select
              value={nodeConfig.countOperator || '>'}
              onChange={(e) => setNodeConfig({ ...nodeConfig, countOperator: e.target.value })}
              size="sm"
            >
              <option value=">">&gt;</option>
              <option value="<">&lt;</option>
              <option value="=">=</option>
              <option value=">=">&gt;=</option>
              <option value="<=">&lt;=</option>
            </Select>
          </Box>

          <Box>
            <FormLabel fontSize="sm">Count Value</FormLabel>
            <Input
              type="number"
              min={0}
              value={nodeConfig.countValue || '0'}
              onChange={(e) => setNodeConfig({ ...nodeConfig, countValue: e.target.value })}
              placeholder="0"
              size="sm"
            />
          </Box>

          <Box p={3} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="lg">
            <FormLabel fontSize="xs">Time Window (Optional)</FormLabel>
            <SimpleGrid columns={2} spacing={2}>
              <Input
                type="number"
                min={1}
                value={nodeConfig.countTimeValue || '7'}
                onChange={(e) => setNodeConfig({ ...nodeConfig, countTimeValue: e.target.value })}
                placeholder="7"
                size="sm"
              />
              <Select
                value={nodeConfig.countTimeUnit || 'days'}
                onChange={(e) => setNodeConfig({ ...nodeConfig, countTimeUnit: e.target.value })}
                size="sm"
              >
                <option value="hours">Hours</option>
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
              </Select>
            </SimpleGrid>
          </Box>

          <Text fontSize="xs" color="gray.600" fontStyle="italic">
            Example: User added to cart more than 3 times in 7 days.
          </Text>
        </VStack>
      )}

      <Divider />

      {/* Branch Outcomes */}
      <Box>
        <FormLabel fontSize="sm" mb={3}>Branch Outcomes</FormLabel>
        <SimpleGrid columns={2} spacing={3}>
          <Box p={3} bg="green.50" border="2px solid" borderColor="green.200" borderRadius="lg">
            <Flex align="center" gap={2} mb={2}>
              <Icon as={FaCheck} color="green.600" />
              <Text fontSize="xs" fontWeight="medium" color="green.900">Yes Path</Text>
            </Flex>
            <Input
              value={nodeConfig.yesLabel || 'Yes'}
              onChange={(e) => setNodeConfig({ ...nodeConfig, yesLabel: e.target.value })}
              placeholder="Yes"
              size="xs"
            />
          </Box>

          <Box p={3} bg="red.50" border="2px solid" borderColor="red.200" borderRadius="lg">
            <Flex align="center" gap={2} mb={2}>
              <Icon as={FaTimes} color="red.600" />
              <Text fontSize="xs" fontWeight="medium" color="red.900">No Path</Text>
            </Flex>
            <Input
              value={nodeConfig.noLabel || 'No'}
              onChange={(e) => setNodeConfig({ ...nodeConfig, noLabel: e.target.value })}
              placeholder="No"
              size="xs"
            />
          </Box>
        </SimpleGrid>

        <Flex align="start" gap={2} mt={3}>
          <Checkbox
            id="fallback"
            isChecked={nodeConfig.enableFallback || false}
            onChange={(e) => setNodeConfig({ ...nodeConfig, enableFallback: e.target.checked })}
            size="sm"
          />
          <FormLabel htmlFor="fallback" fontSize="xs" color="gray.600" cursor="pointer" m={0}>
            Enable Fallback Behavior (If event data unavailable → continue to No Path)
          </FormLabel>
        </Flex>
      </Box>

      <HStack spacing={2}>
        <Button 
          variant="outline" 
          onClick={() => setSelectedNode(null)}
          flex={1}
        >
          Cancel
        </Button>
        <Button 
          colorScheme="orange" 
          onClick={updateNodeData}
          leftIcon={<Icon as={FaSave} />}
          flex={1}
        >
          Update
        </Button>
      </HStack>
    </VStack>
  );
};

export default EnhancedConditionNodeProperties;